import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';

// ── Types ──────────────────────────────────────────────────────────────────────

export type CalendarEventType =
  | 'inspeccion' | 'capacitacion' | 'examen_medico' | 'epp_vencimiento'
  | 'permiso_trabajo' | 'reunion_comite' | 'simulacro' | 'otro';

export type EventStatus = 'pendiente' | 'completado' | 'vencido' | 'cancelado';

export interface CalendarEvent {
  id: string;
  company_id: string;
  title: string;
  event_type: CalendarEventType;
  due_date: string;
  assigned_to: string | null;
  notes: string | null;
  status: EventStatus;
  completed_date: string | null;
  created_at: string;
}

interface Profile { id: string; full_name: string | null; email: string | null; }

interface AutoAlert {
  id: string;
  kind: 'exam' | 'epp' | 'action';
  label: string;
  sub: string;
  dueDate: string;
  urgency: 'expired' | 'warning' | 'ok';
}

// ── Constants ──────────────────────────────────────────────────────────────────

export const EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  inspeccion:      'Inspección',
  capacitacion:    'Capacitación',
  examen_medico:   'Examen médico',
  epp_vencimiento: 'EPP vencimiento',
  permiso_trabajo: 'Permiso de trabajo',
  reunion_comite:  'Reunión comité',
  simulacro:       'Simulacro',
  otro:            'Otro',
};

export const EVENT_TYPE_COLORS: Record<CalendarEventType, { dot: string; bg: string; border: string; text: string }> = {
  inspeccion:      { dot: '#3b82f6', bg: 'rgba(59,130,246,0.15)',   border: 'rgba(59,130,246,0.4)',   text: '#3b82f6' },
  capacitacion:    { dot: '#22c55e', bg: 'rgba(34,197,94,0.15)',    border: 'rgba(34,197,94,0.4)',    text: '#22c55e' },
  examen_medico:   { dot: '#a78bfa', bg: 'rgba(167,139,250,0.15)',  border: 'rgba(167,139,250,0.4)',  text: '#a78bfa' },
  epp_vencimiento: { dot: '#f97316', bg: 'rgba(249,115,22,0.15)',   border: 'rgba(249,115,22,0.4)',   text: '#f97316' },
  permiso_trabajo: { dot: '#eab308', bg: 'rgba(234,179,8,0.15)',    border: 'rgba(234,179,8,0.4)',    text: '#eab308' },
  reunion_comite:  { dot: '#14b8a6', bg: 'rgba(20,184,166,0.15)',   border: 'rgba(20,184,166,0.4)',   text: '#14b8a6' },
  simulacro:       { dot: '#ef4444', bg: 'rgba(239,68,68,0.15)',    border: 'rgba(239,68,68,0.4)',    text: '#ef4444' },
  otro:            { dot: '#94a3b8', bg: 'rgba(148,163,184,0.12)',  border: 'rgba(148,163,184,0.3)',  text: '#94a3b8' },
};

const STATUS_LABELS: Record<EventStatus, string> = {
  pendiente:  'Pendiente',
  completado: 'Completado',
  vencido:    'Vencido',
  cancelado:  'Cancelado',
};

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES   = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// ── Date helpers ───────────────────────────────────────────────────────────────

function toLocalDate(iso: string): Date {
  // Parse YYYY-MM-DD as local date (not UTC)
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function daysUntil(iso: string): number {
  const today = new Date(); today.setHours(0,0,0,0);
  const d = toLocalDate(iso);
  return Math.ceil((d.getTime() - today.getTime()) / 86_400_000);
}

function urgency(iso: string): 'expired' | 'warning' | 'ok' {
  const days = daysUntil(iso);
  if (days < 0) return 'expired';
  if (days <= 7) return 'warning';
  return 'ok';
}

function urgency30(iso: string): 'expired' | 'warning' | 'ok' {
  const days = daysUntil(iso);
  if (days < 0) return 'expired';
  if (days <= 30) return 'warning';
  return 'ok';
}

function fmtDate(iso: string): string {
  return toLocalDate(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Event Modal ────────────────────────────────────────────────────────────────

interface ModalProps {
  event: Partial<CalendarEvent> | null;
  profiles: Profile[];
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}

function EventModal({ event, profiles, companyId, onClose, onSaved }: ModalProps) {
  const isEdit = !!event?.id;

  const [title,         setTitle]         = useState(event?.title ?? '');
  const [eventType,     setEventType]     = useState<CalendarEventType>(event?.event_type ?? 'inspeccion');
  const [dueDate,       setDueDate]       = useState(event?.due_date ?? '');
  const [assignedTo,    setAssignedTo]    = useState(event?.assigned_to ?? '');
  const [notes,         setNotes]         = useState(event?.notes ?? '');
  const [status,        setStatus]        = useState<EventStatus>(event?.status ?? 'pendiente');
  const [completedDate, setCompletedDate] = useState(event?.completed_date ?? '');
  const [errors,        setErrors]        = useState<Record<string, string>>({});
  const [saving,        setSaving]        = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!title.trim()) e.title    = 'El título es requerido';
    if (!dueDate)       e.dueDate  = 'La fecha es requerida';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    const payload = {
      company_id:     companyId,
      title:          title.trim(),
      event_type:     eventType,
      due_date:       dueDate,
      assigned_to:    assignedTo || null,
      notes:          notes.trim() || null,
      status,
      completed_date: completedDate || null,
    };
    const { error } = isEdit
      ? await supabase.from('sso_calendar_events').update(payload).eq('id', event!.id!)
      : await supabase.from('sso_calendar_events').insert(payload);
    setSaving(false);
    if (error) { setErrors({ general: error.message }); return; }
    onSaved();
  };

  const handleMarkComplete = async () => {
    if (!event?.id) return;
    setSaving(true);
    const { error } = await supabase.from('sso_calendar_events').update({
      status: 'completado',
      completed_date: isoDate(new Date()),
    }).eq('id', event.id);
    if (error) {
      alert('Error al marcar como completado: ' + error.message);
      setSaving(false);
      return;
    }
    setSaving(false);
    onSaved();
  };

  // PE-B2b: confirmación dentro del modal de la app (antes, confirm() del navegador). El error se muestra en el modal.
  const handleDelete = async () => {
    if (!event?.id) return;
    setSaving(true);
    const { data, error } = await supabase.from('sso_calendar_events').delete().eq('id', event.id).select('id');
    if (error || !data?.length) {
      setErrors(p => ({ ...p, general: `No se pudo eliminar el evento: ${error?.message ?? 'sin permiso'}.` }));
      setConfirmarBorrar(false);
      setSaving(false);
      return;
    }
    setSaving(false);
    onSaved();
  };

  const col = EVENT_TYPE_COLORS[eventType];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(3px)' }}>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, width: '100%', maxWidth: 500, margin: '0 16px', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: `2px solid ${col.dot}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>{isEdit ? 'Editar evento' : 'Nuevo evento SSO'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {errors.general && <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: '0.85rem' }}>{errors.general}</div>}

          <MField label="Título *" error={errors.title}>
            <input value={title} onChange={e => { setTitle(e.target.value); setErrors(p => ({ ...p, title: '' })); }} placeholder="Descripción del evento" style={inp} />
          </MField>

          <MField label="Tipo de evento *">
            <select value={eventType} onChange={e => setEventType(e.target.value as CalendarEventType)} style={inp}>
              {(Object.keys(EVENT_TYPE_LABELS) as CalendarEventType[]).map(k => (
                <option key={k} value={k}>{EVENT_TYPE_LABELS[k]}</option>
              ))}
            </select>
          </MField>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <MField label="Fecha *" error={errors.dueDate}>
              <input type="date" value={dueDate} onChange={e => { setDueDate(e.target.value); setErrors(p => ({ ...p, dueDate: '' })); }} style={inp} />
            </MField>
            <MField label="Estado">
              <select value={status} onChange={e => setStatus(e.target.value as EventStatus)} style={inp}>
                {(Object.keys(STATUS_LABELS) as EventStatus[]).map(k => (
                  <option key={k} value={k}>{STATUS_LABELS[k]}</option>
                ))}
              </select>
            </MField>
          </div>

          <MField label="Responsable">
            <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} style={inp}>
              <option value="">Sin asignar</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>{p.full_name || p.email || p.id}</option>
              ))}
            </select>
          </MField>

          {isEdit && (
            <MField label="Fecha de completado">
              <input type="date" value={completedDate} onChange={e => setCompletedDate(e.target.value)} style={inp} />
            </MField>
          )}

          <MField label="Notas">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Observaciones, detalles…" style={{ ...inp, resize: 'vertical' }} />
          </MField>
        </div>

        {/* Confirmación de borrado (PE-B2b) */}
        {confirmarBorrar && (
          <div role="alertdialog" aria-labelledby="cal-borrar-titulo" style={{ margin: '0 24px 14px', padding: '12px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p id="cal-borrar-titulo" style={{ margin: 0, color: '#fca5a5', fontSize: '0.85rem' }}>
              ¿Eliminar el evento "{title || 'sin título'}"? Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmarBorrar(false)} disabled={saving} style={cancelBtn}>Cancelar</button>
              <button type="button" onClick={handleDelete} disabled={saving} style={{ padding: '8px 14px', background: '#dc2626', border: 'none', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                {saving ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #334155', display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {isEdit && event?.status !== 'completado' && (
              <button onClick={handleMarkComplete} disabled={saving} style={{ padding: '8px 14px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                ✓ Marcar completado
              </button>
            )}
            {isEdit && (
              <button onClick={() => setConfirmarBorrar(true)} disabled={saving || confirmarBorrar} style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                Eliminar
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={cancelBtn}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.7 : 1 }}>{saving ? 'Guardando…' : isEdit ? 'Guardar' : 'Crear evento'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: '0.79rem', color: '#94a3b8', fontWeight: 600 }}>{label}</label>
      {children}
      {error && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{error}</span>}
    </div>
  );
}

const inp: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
  color: '#e2e8f0', padding: '10px 12px', fontSize: '0.88rem', outline: 'none',
  width: '100%', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
  border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer',
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 18px', background: 'none', border: '1px solid #334155',
  color: '#64748b', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem',
};

// ── Calendar grid ──────────────────────────────────────────────────────────────

function buildCalendarDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const last  = new Date(year, month + 1, 0);
  const days: (Date | null)[] = [];
  // Leading nulls for days before the 1st
  for (let i = 0; i < first.getDay(); i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  // Trailing nulls to complete last row
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

// ── Urgency badge ──────────────────────────────────────────────────────────────

function UrgencyBadge({ u, days }: { u: 'expired' | 'warning' | 'ok'; days: number }) {
  const color = u === 'expired' ? '#ef4444' : u === 'warning' ? '#f59e0b' : '#22c55e';
  const bg    = u === 'expired' ? 'rgba(239,68,68,0.12)' : u === 'warning' ? 'rgba(245,158,11,0.12)' : 'rgba(34,197,94,0.1)';
  const label = u === 'expired' ? `Vencido hace ${Math.abs(days)}d` : u === 'warning' ? `Vence en ${days}d` : `En ${days}d`;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: bg, color, flexShrink: 0 }}>
      {label}
    </span>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SSOCalendarPage() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';
  const canEdit = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;

  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [events,    setEvents]    = useState<CalendarEvent[]>([]);
  const [profiles,  setProfiles]  = useState<Profile[]>([]);
  const [autoAlerts, setAutoAlerts] = useState<AutoAlert[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [modal,     setModal]     = useState<Partial<CalendarEvent> | null | undefined>(undefined);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [mostrarCancelados, setMostrarCancelados] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setFetchError(null);

    const todayIso = isoDate(today);

    const [
      { data: evts, error: evtsErr },
      { data: profs },
      { data: workers },
      { data: eppAsgn },
      { data: incActions },
    ] = await Promise.all([
      supabase.from('sso_calendar_events').select('*').eq('company_id', companyId).order('due_date'),
      supabase.from('profiles').select('id, full_name, email').eq('company_id', companyId).eq('active', true),
      supabase.from('workers').select('id, full_name, last_medical_exam').eq('company_id', companyId).eq('status', 'activo'),
      supabase.from('epp_assignments').select('id, worker_id, epp_id, expiry_date, returned').eq('company_id', companyId).eq('returned', false).not('expiry_date', 'is', null),
      supabase.from('incident_actions').select('id, description, due_date, status, incident_id').eq('company_id', companyId).neq('status', 'completada').not('due_date', 'is', null),
    ]);

    if (evtsErr) { setFetchError(evtsErr.message); setLoading(false); return; }
    setEvents((evts ?? []) as CalendarEvent[]);
    setProfiles((profs ?? []) as Profile[]);

    // Build automatic alerts
    const alerts: AutoAlert[] = [];
    const oneYearAgo = new Date(); oneYearAgo.setFullYear(today.getFullYear() - 1);

    for (const w of (workers ?? [])) {
      if (!w.last_medical_exam || new Date(w.last_medical_exam) < oneYearAgo) {
        const examDate = w.last_medical_exam ?? '2000-01-01';
        const u = urgency30(examDate);
        if (u !== 'ok' || !w.last_medical_exam) {
          alerts.push({
            id: `exam-${w.id}`,
            kind: 'exam',
            label: w.full_name,
            sub: w.last_medical_exam ? `Último examen: ${fmtDate(w.last_medical_exam)}` : 'Sin examen registrado',
            dueDate: w.last_medical_exam ?? todayIso,
            urgency: w.last_medical_exam ? urgency30(w.last_medical_exam) : 'expired',
          });
        }
      }
    }

    // EPP assignments expiring within 30 days or already expired
    const workerMap = new Map((workers ?? []).map((w: { id: string; full_name: string }) => [w.id, w.full_name]));
    for (const a of (eppAsgn ?? [])) {
      if (!a.expiry_date) continue;
      const u = urgency30(a.expiry_date);
      if (u !== 'ok') {
        alerts.push({
          id: `epp-${a.id}`,
          kind: 'epp',
          label: workerMap.get(a.worker_id) ?? 'Trabajador',
          sub: `EPP — vence ${fmtDate(a.expiry_date)}`,
          dueDate: a.expiry_date,
          urgency: u,
        });
      }
    }

    // Incident actions with overdue or near-due dates
    for (const ia of (incActions ?? [])) {
      if (!ia.due_date) continue;
      const u = urgency30(ia.due_date);
      if (u !== 'ok') {
        alerts.push({
          id: `action-${ia.id}`,
          kind: 'action',
          label: ia.description ?? 'Acción correctiva',
          sub: `Vence ${fmtDate(ia.due_date)} · ${ia.status}`,
          dueDate: ia.due_date,
          urgency: u,
        });
      }
    }

    // Sort: expired first, then by date
    alerts.sort((a, b) => {
      if (a.urgency === 'expired' && b.urgency !== 'expired') return -1;
      if (b.urgency === 'expired' && a.urgency !== 'expired') return 1;
      return a.dueDate.localeCompare(b.dueDate);
    });

    setAutoAlerts(alerts);
    setLoading(false);
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  function handleSaved() { setModal(undefined); loadData(); }

  // ── Calendar navigation ────────────────────────────────────────────────────

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
    setSelectedDay(null);
  };

  // ── Build calendar days ────────────────────────────────────────────────────

  const calDays = buildCalendarDays(viewYear, viewMonth);
  const todayIso = isoDate(today);

  // PE-B2b: los eventos cancelados no se muestran por defecto; con el filtro se ven atenuados.
  const cancelados = events.filter(e => e.status === 'cancelado').length;
  const visibles = mostrarCancelados ? events : events.filter(e => e.status !== 'cancelado');

  // Map ISO date → events
  const eventsByDay = new Map<string, CalendarEvent[]>();
  for (const ev of visibles) {
    const key = ev.due_date.slice(0, 10);
    if (!eventsByDay.has(key)) eventsByDay.set(key, []);
    eventsByDay.get(key)!.push(ev);
  }

  // Upcoming events in next 30 days (normalize due_date to YYYY-MM-DD for safe comparison)
  const cutoff = isoDate(new Date(today.getTime() + 30 * 86_400_000));
  const upcoming = events
    .filter(e => e.due_date.slice(0, 10) >= todayIso && e.due_date.slice(0, 10) <= cutoff && e.status !== 'completado' && e.status !== 'cancelado')
    .slice(0, 20);

  // Selected day events
  const selectedEvents = selectedDay ? (eventsByDay.get(selectedDay) ?? []) : [];

  const profileMap = new Map(profiles.map(p => [p.id, p.full_name || p.email || '']));

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <span style={{ color: '#64748b' }}>Cargando calendario…</span>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div style={{ padding: 24, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, color: '#ef4444', fontSize: '0.9rem' }}>
        Error al cargar eventos: {fetchError}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#e2e8f0' }}>Calendario SSO</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Eventos, vencimientos y obligaciones de seguridad</p>
        </div>
        {cancelados > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, color: '#94a3b8', fontSize: '0.82rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={mostrarCancelados} onChange={e => setMostrarCancelados(e.target.checked)} style={{ width: 18, height: 18 }} />
            Mostrar cancelados ({cancelados})
          </label>
        )}
        {canEdit && (
          <button onClick={() => setModal({})} style={primaryBtn}>
            + Agregar evento
          </button>
        )}
      </div>

      {/* Main layout: calendar + sidebar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 20, alignItems: 'start' }}>

        {/* ── Calendar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Month nav */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #334155' }}>
              <button onClick={prevMonth} style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.9rem' }}>‹</button>
              <span style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '1rem' }}>
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button onClick={nextMonth} style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '0.9rem' }}>›</button>
            </div>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: '1px solid #334155' }}>
              {DAY_NAMES.map(d => (
                <div key={d} style={{ padding: '8px 4px', textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
              {calDays.map((day, i) => {
                if (!day) return <div key={i} style={{ minHeight: 72, borderRight: i % 7 !== 6 ? '1px solid #1e2d3d' : 'none', borderBottom: '1px solid #1e2d3d', background: '#0f172a' }} />;
                const iso = isoDate(day);
                const dayEvts = eventsByDay.get(iso) ?? [];
                const isToday = iso === todayIso;
                const isSelected = iso === selectedDay;
                return (
                  <div
                    key={i}
                    onClick={() => setSelectedDay(iso === selectedDay ? null : iso)}
                    style={{
                      minHeight: 72, padding: '6px 6px 4px',
                      borderRight: i % 7 !== 6 ? '1px solid #1e2d3d' : 'none',
                      borderBottom: '1px solid #1e2d3d',
                      cursor: 'pointer',
                      background: isSelected ? 'rgba(47,191,113,0.06)' : 'transparent',
                      transition: 'background 0.15s',
                      position: 'relative',
                    }}
                  >
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isToday ? '#2FBF71' : 'transparent',
                      color: isToday ? '#fff' : '#94a3b8',
                      fontWeight: isToday ? 700 : 400,
                      fontSize: '0.82rem', marginBottom: 4,
                    }}>
                      {day.getDate()}
                    </div>
                    {/* Event dots */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {dayEvts.slice(0, 4).map((ev, ei) => {
                        const col = EVENT_TYPE_COLORS[ev.event_type];
                        return (
                          <div
                            key={ei}
                            title={ev.title}
                            style={{
                              maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontSize: '0.65rem', fontWeight: 600, padding: '1px 5px', borderRadius: 10,
                              background: col.bg, color: col.text, border: `1px solid ${col.border}`,
                              opacity: ev.status === 'cancelado' ? 0.45 : 1,
                              textDecoration: ev.status === 'cancelado' ? 'line-through' : 'none',
                            }}
                          >
                            {ev.title.length > 10 ? ev.title.slice(0, 9) + '…' : ev.title}
                          </div>
                        );
                      })}
                      {dayEvts.length > 4 && (
                        <div style={{ fontSize: '0.63rem', color: '#64748b', paddingTop: 1 }}>+{dayEvts.length - 4}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected day detail */}
          {selectedDay && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#e2e8f0' }}>
                  {toLocalDate(selectedDay).toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
                {canEdit && (
                  <button onClick={() => setModal({ due_date: selectedDay })} style={{ padding: '5px 12px', background: 'rgba(47,191,113,0.1)', border: '1px solid rgba(47,191,113,0.3)', color: '#2FBF71', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                    + Evento
                  </button>
                )}
              </div>
              {selectedEvents.length === 0 ? (
                <p style={{ margin: 0, color: '#475569', fontSize: '0.85rem' }}>Sin eventos este día.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedEvents.map(ev => {
                    const col = EVENT_TYPE_COLORS[ev.event_type];
                    return (
                      <div
                        key={ev.id}
                        onClick={() => canEdit && setModal(ev)}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8, background: col.bg, border: `1px solid ${col.border}`, cursor: canEdit ? 'pointer' : 'default', opacity: ev.status === 'cancelado' ? 0.55 : 1 }}
                      >
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: col.dot, marginTop: 5, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#e2e8f0' }}>{ev.title}</div>
                          <div style={{ fontSize: '0.76rem', color: col.text }}>{EVENT_TYPE_LABELS[ev.event_type]}</div>
                          {ev.assigned_to && <div style={{ fontSize: '0.74rem', color: '#64748b' }}>Responsable: {profileMap.get(ev.assigned_to) ?? '—'}</div>}
                        </div>
                        <StatusPill s={ev.status} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Color legend */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '14px 18px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '0.76rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Leyenda</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
              {(Object.keys(EVENT_TYPE_LABELS) as CalendarEventType[]).map(k => {
                const col = EVENT_TYPE_COLORS[k];
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.dot, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{EVENT_TYPE_LABELS[k]}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right sidebar ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Upcoming events */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #334155' }}>
              <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#e2e8f0' }}>Próximos 30 días</h3>
            </div>
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              {upcoming.length === 0 ? (
                <p style={{ margin: 0, padding: '16px', color: '#475569', fontSize: '0.83rem' }}>Sin eventos próximos.</p>
              ) : (
                upcoming.map(ev => {
                  const col = EVENT_TYPE_COLORS[ev.event_type];
                  const days = daysUntil(ev.due_date);
                  const u = urgency(ev.due_date);
                  return (
                    <div
                      key={ev.id}
                      onClick={() => canEdit && setModal(ev)}
                      style={{ display: 'flex', gap: 10, padding: '10px 14px', borderBottom: '1px solid #1a2538', cursor: canEdit ? 'pointer' : 'default', transition: 'background 0.15s' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ width: 3, borderRadius: 2, background: col.dot, alignSelf: 'stretch', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                        <div style={{ fontSize: '0.73rem', color: col.text }}>{EVENT_TYPE_LABELS[ev.event_type]}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{fmtDate(ev.due_date)}</div>
                      </div>
                      <UrgencyBadge u={u} days={days} />
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Auto alerts */}
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#e2e8f0' }}>Alertas automáticas</h3>
              {autoAlerts.length > 0 && (
                <span style={{ padding: '2px 8px', borderRadius: 20, background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: '0.72rem', fontWeight: 700 }}>{autoAlerts.length}</span>
              )}
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {autoAlerts.length === 0 ? (
                <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#22c55e', fontSize: '1rem' }}>✓</span>
                  <span style={{ color: '#475569', fontSize: '0.83rem' }}>Sin alertas pendientes.</span>
                </div>
              ) : (
                autoAlerts.map(al => {
                  const days = daysUntil(al.dueDate);
                  const iconMap = { exam: '🩺', epp: '🦺', action: '⚡' };
                  return (
                    <div key={al.id} style={{ padding: '10px 14px', borderBottom: '1px solid #1a2538', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '0.95rem', flexShrink: 0, marginTop: 1 }}>{iconMap[al.kind]}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{al.label}</div>
                        <div style={{ fontSize: '0.73rem', color: '#64748b', marginTop: 2 }}>{al.sub}</div>
                      </div>
                      <UrgencyBadge u={al.urgency} days={days} />
                    </div>
                  );
                })
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Alertas section heading for alerts that need broader context */}
      {autoAlerts.filter(a => a.urgency === 'expired').length > 0 && (
        <div style={{ padding: '12px 18px', borderRadius: 10, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, flexShrink: 0 }}>⚠</span>
          <span>
            {autoAlerts.filter(a => a.urgency === 'expired').length} alerta{autoAlerts.filter(a => a.urgency === 'expired').length !== 1 ? 's' : ''} vencida{autoAlerts.filter(a => a.urgency === 'expired').length !== 1 ? 's' : ''} — revisa el panel lateral para tomar acción.
          </span>
        </div>
      )}

      {/* Modal */}
      {modal !== undefined && (
        <EventModal
          event={modal}
          profiles={profiles}
          companyId={companyId}
          onClose={() => setModal(undefined)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Status pill ────────────────────────────────────────────────────────────────

function StatusPill({ s }: { s: EventStatus }) {
  const cfg: Record<EventStatus, { color: string; bg: string }> = {
    pendiente:  { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
    completado: { color: '#22c55e', bg: 'rgba(34,197,94,0.1)'  },
    vencido:    { color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
    cancelado:  { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  };
  const c = cfg[s];
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700, background: c.bg, color: c.color, flexShrink: 0, alignSelf: 'flex-start' }}>
      {STATUS_LABELS[s]}
    </span>
  );
}
